import appConfig from 'app-config';
import { useTranslator } from '@jutro/locale';
import QBErrorMessages from './MLPupLineValidatorUtil.messages.js';
import GPAErrorMessages from './MLPupLineValidatorUtil-GPA.messages';
import AMPErrorMessages from './MLPupLineValidatorUtil-AMP.messages';

function MLPupLineValidatorUtil(){
    const translator = useTranslator();

    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    let errorMessages;
    if(isGPA){
        errorMessages = GPAErrorMessages;
    }else if(isAMP){
        errorMessages = AMPErrorMessages;
    }else{
        errorMessages = QBErrorMessages;
    }

    const ERROR_MESSAGES = {
        "NO_COVERABLE_FOR_POLICY": errorMessages.noRecoverableForPolicy,
        "VEHICLE_MESSAGES": {
            "NO_COVERABLE": errorMessages.policyAutoListedButNoVehicle,
            "NO_UL_POLICY": errorMessages.vhicleListedButNoPolicyAuto
        },
        "DRIVER_MESSAGES": {
            "NO_COVERABLE": {
                "PA": errorMessages.policyAutoListedButNoDriver,
                "motorcycle_ml": errorMessages.policyMotorcycleListedButNoDriver,
                "recreational_vehicle_ml": errorMessages.policyRecreationalListedButNoDriver
            },
            "NO_UL_POLICY": errorMessages.driverListedButNoULAutoRecreationalMotorcycle
        },
        "MOTORCYCLE_MESSAGES": {
            "NO_COVERABLE": errorMessages.policyMotorcycleListedButNoMotorcycle,
            "NO_UL_POLICY": errorMessages.motorcycleListedButNoMotorcyclePolicy
        },
        "REC_VEHICLE_MESSAGES": {
            "NO_COVERABLE":errorMessages.policyRecreationalButNoRecreationalVehicle,
            "NO_UL_POLICY": errorMessages.recreationalVehicleButNoRecreationalVehiclePolicy
        },
        "OTH_PERSONAL_MESSAGES": {
            "NO_UL_POLICY": errorMessages.OPRListedButNoAutoPolicy
        },
        "OTH_PERSONAL_MESSAGES_AMP": {
            "NO_UL_POLICY": errorMessages.OPRListedButNoAutoPolicyAMP
        },
        "WATERCRAFT_OPERATORS_MESSAGES": {
            "NO_COVERABLE": errorMessages.watercraftOperatorNoCoverable,
            "NO_UL_POLICY": errorMessages.watercraftOperatorNoUlPol
        },
        "WATERCRAFT_MESSAGES": {
            "NO_COVERABLE": errorMessages.watercraftNoCoverable,
            "NO_UL_POLICY": errorMessages.watercraftNoUlPol,
            "NO_QUESTIONS": errorMessages.watercraftNoQuestions
        },
        "LOCATION_MESSAGES": {
            "NO_COVERABLE": errorMessages.locationNoCoverable,
            "NO_UL_POLICY": errorMessages.locationNoUlPolicy
        },
        "BUSINESS_ACTIVITIES_MESSAGES": {
            "NO_UL_POLICY": errorMessages.businessActivitiesNoUlPol
        }
    }

    const UL_POLICY_CODES = {
        WATERCRAFT: 'PWL',
        HOME: 'HO',
        AUTO: 'PA',
        MOTORCYCLE: 'motorcycle_ml',
        RECREATIONAL_VEHICLE: 'recreational_vehicle_ml'
    };

    const getNameOfTypeCode = (typeKeyName) => {
        return translator({
            id: typeKeyName,
            defaultMessage: typeKeyName
        });
    }

    const _validateCoverableExposures = (model, ulPolicies, messages) => {
        const error = [];
        if (ulPolicies && ulPolicies.length > 0) {
            if (!model || model.length === 0) {
                error.push(messages.NO_COVERABLE);
            } else {
                ulPolicies.forEach((ul) => {
                    const coverableAttachedToUL = model.filter((cov) => cov.value.ulPolicyFixID === ul.value.ulPolicyFixID);
                    if (!coverableAttachedToUL || coverableAttachedToUL.length === 0) {
                        const ulPolicyType = getNameOfTypeCode(ul.policyType.value.name);
                        error.push({...ERROR_MESSAGES.NO_COVERABLE_FOR_POLICY, args: {policyNumber: ul.value.policyNumber, policyType: ulPolicyType}});
                    }
                });
            }
        } else if (model.length > 0) {
            error.push(messages.NO_UL_POLICY);
        }
        return error;
    };    

    const _validateDrivers = (drivers, ulPolicies, messages) => {
        const error = [];
        if (ulPolicies && ulPolicies.length > 0) {
            if (!drivers || drivers.length === 0) {
                ulPolicies.forEach((ulPolicy) => {
                    error.push(messages.NO_COVERABLE[ulPolicy.value.policyType]);
                });
            }
        } else if (drivers.length > 0) {
            error.push(messages.NO_UL_POLICY);
        }
        return error;
    };

    const _validateDriverExposures = (pupModel) => {
        const validPolicyTypeCodes = [
            UL_POLICY_CODES.AUTO,
            UL_POLICY_CODES.RECREATIONAL_VEHICLE,
            UL_POLICY_CODES.MOTORCYCLE
        ];
        const ulPolicies = pupModel.underlyingPolicies.children.filter((ul) => {
            return validPolicyTypeCodes.some((policyType) => policyType === ul.value.policyType);
        });
        const drivers = pupModel.coverables.vehicleOperators.children.filter((vehicleOperator) => vehicleOperator.value.driverType === 'rated');
        return _validateDrivers(drivers, ulPolicies, ERROR_MESSAGES.DRIVER_MESSAGES);
    };
    
    const _validateMotorcycleVehicleExposures = (pupModel) => {
        const ulPolicies = pupModel.underlyingPolicies.children.filter((ul) => ul.value.policyType === UL_POLICY_CODES.MOTORCYCLE);
        const motorcycles = pupModel.coverables.vehicles.children.filter((veh) => veh.value.type === 'Motorcycle');
        return _validateCoverableExposures(motorcycles, ulPolicies, ERROR_MESSAGES.MOTORCYCLE_MESSAGES);
    };

    const _validateRECVehicleExposures = (pupModel) => {
        const ulPolicies = pupModel.underlyingPolicies.children.filter((ul) => ul.value.policyType === UL_POLICY_CODES.RECREATIONAL_VEHICLE);
        const recVehCodes = ['GolfCart_ML', 'atv_snowmobile_ml', 'Recreational'];
        const recVehicles = pupModel.coverables.vehicles.children.filter((veh) => recVehCodes.indexOf(veh.value.type) > -1);
        return _validateCoverableExposures(recVehicles, ulPolicies, ERROR_MESSAGES.REC_VEHICLE_MESSAGES);
    };

    const _validateOtherPersonalVehicleExposures = (pupModel, isEndorsement) => {
        const ulPolicies = pupModel.underlyingPolicies.children.filter((ul) => ul.value.policyType === UL_POLICY_CODES.AUTO);
        const otherPersonalVehicle = pupModel.coverables.vehicles.children.filter((veh) => veh.value.type === 'Other_Personally_Registered');
        if (ulPolicies && ulPolicies.length === 0 && otherPersonalVehicle.length > 0) {
            return isAMP || (isGPA && isEndorsement) ? [ERROR_MESSAGES.OTH_PERSONAL_MESSAGES_AMP.NO_UL_POLICY] : 
                [ERROR_MESSAGES.OTH_PERSONAL_MESSAGES.NO_UL_POLICY];
        }
        return [];
    };

    const _validatePACoverableExposures = (model, ulPolicies, messages) => {
        const error = [];
        if (ulPolicies && ulPolicies.length > 0) {
            if ((!model || model.length === 0) && !ulPolicies.every((pol) => {return pol.value.namedNonOwner_Ext;})) {
                error.push(messages.NO_COVERABLE);
            } else {
                ulPolicies.forEach((ul)=> {
                    const coverableAttachedToUL = model.filter((cov) => cov.value.ulPolicyFixID === ul.value.ulPolicyFixID);
                    if ((!coverableAttachedToUL || coverableAttachedToUL.length === 0) && !ul.value.namedNonOwner_Ext) {
                        const ulPolicyType = getNameOfTypeCode(ul.policyType.value.name);
                        error.push({...ERROR_MESSAGES.NO_COVERABLE_FOR_POLICY, args: {policyNumber: ul.value.policyNumber, policyType: ulPolicyType}});
                    }
                });
            }
        } else if (model.length > 0) {
            error.push(messages.NO_UL_POLICY);
        }
        return error;
    };

    const _validatePAVehicleExposures = (pupModel) => {
        const ulPolicies = pupModel.underlyingPolicies.children.filter((ul) => ul.value.policyType === UL_POLICY_CODES.AUTO);
        const nonPAVehicles = ['Motorcycle', 'Moped', 'GolfCart_ML', 'atv_snowmobile_ml', 'Recreational', 'Other_Personally_Registered'];
        const vehicles = pupModel.coverables.vehicles.children.filter((veh) => nonPAVehicles.indexOf(veh.value.type) < 0);
        return _validatePACoverableExposures(vehicles, ulPolicies, ERROR_MESSAGES.VEHICLE_MESSAGES);
    };

    const _validateWatercraftOperatorExposures = (pupModel) => {
        const error = [];
        const ulPolicies = pupModel.underlyingPolicies.children.filter((ul) => ul.value.policyType === UL_POLICY_CODES.WATERCRAFT);
        const watercraftOperators = pupModel.coverables.watercraftOperators.children;
        if (ulPolicies && ulPolicies.length > 0) {
            if (!watercraftOperators || watercraftOperators.length === 0) {
                error.push(ERROR_MESSAGES.WATERCRAFT_OPERATORS_MESSAGES.NO_COVERABLE);
            }
        } else if (watercraftOperators.length > 0) {
            error.push(ERROR_MESSAGES.WATERCRAFT_OPERATORS_MESSAGES.NO_UL_POLICY);
        }
        return error;
    };

    const _validateWatercraftExposures = (pupModel) => {
        const ulPolicies = pupModel.underlyingPolicies.children.filter((ul) => ul.value.policyType === UL_POLICY_CODES.WATERCRAFT);
        const watercraftExposures = pupModel.coverables.watercrafts.children;

        const errors = _validateCoverableExposures(watercraftExposures, ulPolicies, ERROR_MESSAGES.WATERCRAFT_MESSAGES);

        // Revisit below in AMP
        // if (isAMP && watercraftExposures.length > 0) {
        //     const watercraftQuestionSet = watercraftExposures.find((watercraft) => watercraft.watercraftQuestionSet).watercraftQuestionSet || [];
        //     if (watercraftQuestionSet) {
        //      if (QuestionSetService.checkForErrors(watercraftQuestionSet)) {
        //     errors.push($filter('translate')(ERROR_MESSAGES.WATERCRAFT_MESSAGES.NO_QUESTIONS));
        //      }
        //     }
        // }
        return errors;
    };

    const _hasNotPrimaryLocationDefined = (locations) => {
        return locations && locations.length > 0 && !locations.some((location) => location.value.dwellingUsage === 'prim');
    };

    const _validateLocationExposures = (pupModel) => {
        const ulPolicies = pupModel.underlyingPolicies.children.filter((ul) => ul.value.policyType === UL_POLICY_CODES.HOME);
        const locations = pupModel.coverables.locations.children;
        const errors = _validateCoverableExposures(locations, ulPolicies, ERROR_MESSAGES.LOCATION_MESSAGES);
        if (_hasNotPrimaryLocationDefined(locations)) {
            errors.push(errorMessages.primaryLocationErr);
        }
        return errors;
    };


    const validateVehicleDriverDetailsExposures = (pupModel) => {
        const errors = [].concat(
            _validateDriverExposures(pupModel),
            _validatePAVehicleExposures(pupModel),
            _validateMotorcycleVehicleExposures(pupModel),
            _validateRECVehicleExposures(pupModel),
            _validateOtherPersonalVehicleExposures(pupModel, false)
        );
        return [...new Set(errors)];
    };

    const _validateBusinessActivitiesExposures = (pupModel) => {
        const ulPolicies = pupModel.underlyingPolicies.children.filter((ul) => ul.value.policyType === 'HO');
        const businessActivities = pupModel.coverables.businessActivities.children;
        return ulPolicies && ulPolicies.length === 0 && businessActivities.length > 0 ? [ERROR_MESSAGES.BUSINESS_ACTIVITIES_MESSAGES.NO_UL_POLICY] : [];
    };

    const endorsementValidateAllExposures = (pupModel) => {
        const errors = [].concat(
            _validateDriverExposures(pupModel),
            _validateWatercraftExposures(pupModel),
            _validateWatercraftOperatorExposures(pupModel),
            _validateLocationExposures(pupModel),
            _validatePAVehicleExposures(pupModel),
            _validateMotorcycleVehicleExposures(pupModel),
            _validateRECVehicleExposures(pupModel),
            _validateOtherPersonalVehicleExposures(pupModel, true),
            _validateBusinessActivitiesExposures(pupModel)
        );
        return [...new Set(errors)];
    };

    const isLocationPrimary = (location) => {
        return location.dwellingUsage && location.dwellingUsage.value && location.dwellingUsage.value.code === 'prim';
    };

    const _hasMoreThanOnePrimaryLocationDefined = (locations) => {
        let numOfPrimary = 0;
        if (locations.length <= 1) {
            return false;
        }
        if (locations.length > 1) {
            locations.forEach((location) => {
                if (isLocationPrimary(location)) {
                    numOfPrimary++;
                }
            });
            return numOfPrimary > 1;
        }
    };

    const _isPrimaryLocationStateDifferentFromInsuredState = (location, policyAddress) => {
        return location.dwellingUsage.value !== undefined &&
            location.dwellingUsage.value.code === 'prim' &&
            location.address.state.value.code !== policyAddress.state.value.code;
    };

    const validatePrimaryLocation = (currentSelectedLocation, allLocations, policyAddress) => {
        if (isLocationPrimary(currentSelectedLocation) && _hasMoreThanOnePrimaryLocationDefined(allLocations)) {
            return errorMessages.onlyOnePrimaryHomeIsAllowed;
        } 
        if (_isPrimaryLocationStateDifferentFromInsuredState(currentSelectedLocation, policyAddress)) {
            return errorMessages.primaryHomeStateMustMatchAddressState;
        }
        return null;
    };

    const validateWatercraftLocationBusinessDetailsExposures = (pupModel) => {
        const errors = [].concat(
            _validateWatercraftExposures(pupModel),
            _validateWatercraftOperatorExposures(pupModel),
            _validateLocationExposures(pupModel),
            _validateBusinessActivitiesExposures(pupModel)
        );
        return [...new Set(errors)];
    };

    return {
        validateVehicleDriverDetailsExposures: validateVehicleDriverDetailsExposures,
        validateWatercraftLocationBusinessDetailsExposures: validateWatercraftLocationBusinessDetailsExposures,
        endorsementValidateAllExposures,
        validatePrimaryLocation
    };
}

export default MLPupLineValidatorUtil;